import React, { useState } from 'react';
import { Shield, Zap, Package, CheckCircle, ChevronDown, ChevronUp, Info } from 'lucide-react';

// ── Données SAV officielles (Cahier des Charges 2026) ─────────────────────────
const GUARANTEES = [
  {
    label: "Appareils Neufs / Reconditionnés",
    sublabel: "Art. L217-3 Code Consommation",
    duration: "24 mois",
    durationShort: "2 ans",
    color: "from-emerald-500/20 to-emerald-600/10 border-emerald-500/40",
    badgeColor: "bg-emerald-500",
    textColor: "text-emerald-400",
    icon: "🏆",
    covered: ["Défauts de fabrication", "Panne matérielle", "Défaut d'allumage"],
  },
  {
    label: "Pièces d'Origine (Service Pack)",
    sublabel: "Qualité OEM certifiée",
    duration: "12 mois",
    durationShort: "1 an",
    color: "from-blue-500/20 to-blue-600/10 border-blue-500/40",
    badgeColor: "bg-blue-500",
    textColor: "text-blue-400",
    icon: "⭐",
    covered: ["Pixels morts sans choc", "Batterie gonflement prématuré", "Tactile défaillant"],
  },
  {
    label: "Compatibles Premium",
    sublabel: "Qualité supérieure vérifiée",
    duration: "6 mois",
    durationShort: "6 mois",
    color: "from-orange-500/20 to-orange-600/10 border-orange-500/40",
    badgeColor: "bg-orange-500",
    textColor: "text-orange-400",
    icon: "✅",
    covered: ["Défauts internes de la pièce", "Scintillement anormal", "Zone tactile morte"],
  },
  {
    label: "Entrée de Gamme",
    sublabel: "Minimum légal garanti",
    duration: "3 mois",
    durationShort: "3 mois",
    color: "from-slate-500/20 to-slate-600/10 border-slate-500/40",
    badgeColor: "bg-slate-500",
    textColor: "text-slate-400",
    icon: "🔧",
    covered: ["Défauts de fabrication directs", "Panne à l'installation"],
  },
];

const EXCLUSIONS = [
  { icon: "💥", label: "Chute ou impact (vitre brisée, coins enfoncés)" },
  { icon: "💧", label: "Oxydation / contact avec l'eau" },
  { icon: "🔩", label: "Ouverture par un tiers (vis marquées, sceau rompu)" },
  { icon: "🔄", label: "Pièce ramenée seule hors de l'appareil" },
];

const STRENGTHS = [
  { icon: <Zap size={18} className="text-yellow-400" />, title: "Express 15 min", desc: "Réparation sous vos yeux, sans rendez-vous" },
  { icon: <Package size={18} className="text-blue-400" />, title: "Stock immédiat", desc: "iPhone, Samsung, Xiaomi, Huawei en stock" },
  { icon: <Shield size={18} className="text-emerald-400" />, title: "Garantie écrite", desc: "Document remis à chaque intervention" },
  { icon: <CheckCircle size={18} className="text-purple-400" />, title: "Diagnostic GRATUIT", desc: "Devis avant tout travail, sans engagement" },
];

export const PricingGuarantee = () => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [showExclusions, setShowExclusions] = useState(false);

  const scrollToQuote = () => {
    const el = document.getElementById('quote');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="tarification" className="relative py-20 overflow-hidden">
      {/* Fond semi-transparent compatible avec les thèmes HD */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* En-tête */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-5 py-1.5 mb-5">
            <Shield size={15} className="text-emerald-400" />
            <span className="text-white/80 text-sm font-semibold tracking-wide">Garanties Officielles 2026</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Tarification Transparente<br className="hidden md:block" /> & Garanties Légales
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            Nos tarifs s'adaptent à votre budget. Chaque intervention est couverte
            par une <strong className="text-white/90">garantie légale écrite</strong> — de 3 mois à 2 ans selon la qualité de la pièce choisie.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-start">

          {/* ── Tableau des garanties (3/5) ─────────────────────── */}
          <div className="lg:col-span-3">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Shield size={20} className="text-emerald-400" />
              Nos 4 niveaux de garantie
            </h3>

            <div className="space-y-2">
              {GUARANTEES.map((g, i) => (
                <div key={i} className={`rounded-xl border bg-gradient-to-r ${g.color} overflow-hidden transition-all duration-200`}>
                  {/* Ligne principale */}
                  <button
                    className="w-full flex items-center justify-between px-4 py-3.5 text-left"
                    onClick={() => setExpandedRow(expandedRow === i ? null : i)}
                    data-testid={`guarantee-row-${i}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${g.badgeColor}`} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{g.icon}</span>
                          <span className="font-semibold text-white text-sm sm:text-base truncate">{g.label}</span>
                        </div>
                        <span className="text-xs text-white/40">{g.sublabel}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      <span className={`font-bold text-base sm:text-lg ${g.textColor} whitespace-nowrap`}>{g.duration}</span>
                      {expandedRow === i
                        ? <ChevronUp size={14} className="text-white/50" />
                        : <ChevronDown size={14} className="text-white/50" />
                      }
                    </div>
                  </button>

                  {/* Détails (expandable) */}
                  {expandedRow === i && (
                    <div className="px-4 pb-3.5 border-t border-white/10">
                      <p className="text-xs text-white/50 mt-2 mb-1.5 font-semibold uppercase tracking-wide">Couvre notamment :</p>
                      <ul className="space-y-1">
                        {g.covered.map((c, j) => (
                          <li key={j} className="flex items-center gap-2 text-xs text-white/70">
                            <span className="w-1 h-1 rounded-full bg-white/40 flex-shrink-0" />
                            {c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Note Art. L217-11 */}
            <div className="mt-4 flex items-start gap-2.5 bg-blue-500/10 border border-blue-500/25 rounded-xl px-4 py-3">
              <Info size={15} className="text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-white/60 leading-relaxed">
                <strong className="text-blue-400">Art. L217-11 Code Consommation :</strong> Si votre appareil est immobilisé
                plus de 7 jours chez nous pendant la garantie, celle-ci est automatiquement prolongée de la durée d'immobilisation.
              </p>
            </div>

            {/* Exclusions (toggle) */}
            <button
              onClick={() => setShowExclusions(v => !v)}
              className="mt-3 flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors"
              data-testid="toggle-exclusions"
            >
              {showExclusions ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              {showExclusions ? 'Masquer' : 'Voir'} les cas de non-garantie
            </button>

            {showExclusions && (
              <div className="mt-2 bg-red-500/10 border border-red-500/25 rounded-xl px-4 py-3">
                <p className="text-xs text-red-400 font-semibold mb-2 uppercase tracking-wide">Exclusions (garantie caduque) :</p>
                <ul className="space-y-1.5">
                  {EXCLUSIONS.map((e, i) => (
                    <li key={i} className="flex items-center gap-2 text-xs text-white/60">
                      <span>{e.icon}</span>
                      {e.label}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-white/30 mt-2 italic">
                  Clauses conformes Art. 1103 & 2286 Code Civil
                </p>
              </div>
            )}
          </div>

          {/* ── Points forts + CTA (2/5) ────────────────────────── */}
          <div className="lg:col-span-2 space-y-4">
            <h3 className="text-xl font-bold text-white mb-1">
              Nos engagements
            </h3>

            {STRENGTHS.map((s, i) => (
              <div key={i} className="flex items-start gap-3 bg-white/5 border border-white/10 backdrop-blur-md rounded-xl p-4 hover:bg-white/8 transition-colors">
                <div className="w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  {s.icon}
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">{s.title}</p>
                  <p className="text-xs text-white/50 mt-0.5">{s.desc}</p>
                </div>
              </div>
            ))}

            {/* Mention dynamique */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
              <p className="text-xs text-amber-300/90 leading-relaxed">
                <strong className="text-amber-400">Prix variables selon qualité :</strong> Le tarif varie selon la largeur
                des produits et quantités. Rapport qualité-prix et rapidité garantis.
              </p>
            </div>

            {/* CTA */}
            <div className="space-y-2 pt-2">
              <button
                onClick={scrollToQuote}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white px-6 py-3.5 rounded-xl font-bold text-sm transition-all hover:scale-[1.02] shadow-lg shadow-blue-600/30"
                data-testid="guarantee-cta-quote"
              >
                Devis gratuit en 2 minutes
              </button>
              <a
                href="tel:+33426008210"
                className="flex items-center justify-center gap-2 w-full border border-white/20 text-white/80 hover:bg-white/10 px-6 py-3.5 rounded-xl font-semibold text-sm transition-all"
                data-testid="guarantee-cta-phone"
              >
                📞 04 26 00 82 10
              </a>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};
